/**
 * NotificationBell Component - Displays notifications in a dropdown.
 */
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Bell, Star, Phone, Trophy, Award, Flame, Crown } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../utils/api";
import { formatDate } from "../../utils/dateTime";

// Notification Icon Helper
const getNotificationIcon = (iconName, size = 16, className = "") => {
  const icons = {
    bell: Bell,
    star: Star,
    phone: Phone,
    trophy: Trophy,
    award: Award,
    flame: Flame,
    crown: Crown
  };
  const IconComponent = icons[iconName] || Bell;
  return <IconComponent size={size} className={className} />;
};

// Notification Color Helper
const getNotificationColorClasses = (color) => {
  const colors = {
    blue: { bg: "bg-blue-100", text: "text-blue-600" },
    amber: { bg: "bg-amber-100", text: "text-amber-600" },
    purple: { bg: "bg-purple-100", text: "text-purple-600" },
    green: { bg: "bg-green-100", text: "text-green-600" },
    emerald: { bg: "bg-emerald-100", text: "text-emerald-600" },
    rose: { bg: "bg-rose-100", text: "text-rose-600" },
    red: { bg: "bg-red-100", text: "text-red-600" },
    gold: { bg: "bg-amber-100", text: "text-amber-600" },
    orange: { bg: "bg-orange-100", text: "text-orange-600" }
  };
  return colors[color] || colors.blue;
};

// Time ago helper
const getTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "upravo sada";
  if (diffMins < 60) return `prije ${diffMins} min`;
  if (diffHours < 24) return `prije ${diffHours}h`;
  if (diffDays < 7) return `prije ${diffDays}d`;
  return formatDate(date);
};

export const NotificationBell = ({ className = "" }) => {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count periodically
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!token) return;
      try {
        const response = await apiClient.get('/notifications/unread-count');
        setUnreadCount(response.data.unread_count);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Fetch full notifications when dropdown opens
  const fetchNotifications = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await apiClient.get('/notifications?limit=20');
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await apiClient.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiClient.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success("Sve notifikacije označene kao pročitane");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={handleToggle}
        data-testid="notification-bell"
        className="relative p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1"
            data-testid="notification-count"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-sm">Obavijesti</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-[#1B3F8F] hover:underline"
                data-testid="mark-all-read"
              >
                Označi sve kao pročitano
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                Učitavanje...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Nema obavijesti</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const colors = getNotificationColorClasses(notification.color);
                return (
                  <div
                    key={notification.id}
                    onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
                    data-testid={`notification-item-${notification.id}`}
                    className={`p-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !notification.is_read ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                        {getNotificationIcon(notification.icon, 18, colors.text)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!notification.is_read ? "text-gray-900" : "text-gray-600"}`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="w-2 h-2 rounded-full bg-[#1B3F8F] flex-shrink-0 mt-1.5"></span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{getTimeAgo(notification.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
